type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type AnalyticsConsent = 'granted' | 'denied' | 'unset';
export const ANALYTICS_CONSENT_KEY = 'modelmap:analytics-consent';
const MEASUREMENT_ID = 'G-GRKL4Y9RQC';

export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return 'unset';
  if (window.navigator.doNotTrack === '1') return 'denied';
  const stored = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  return stored === 'granted' || stored === 'denied' ? stored : 'unset';
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, 'unset'>): void {
  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
}

export function initialiseAnalytics(): void {
  if (typeof window === 'undefined' || getAnalyticsConsent() !== 'granted' || window.gtag) return;
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_ANALYTICS !== 'true') return;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = (...args: unknown[]) => window.dataLayer?.push(args);
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  script.dataset.modelmapAnalytics = 'true';
  document.head.appendChild(script);
}

function canTrack(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_ANALYTICS !== 'true') return false;
  if (getAnalyticsConsent() !== 'granted') return false;
  return typeof window.gtag === 'function';
}

export function trackPageView(path: string): void {
  if (!canTrack()) return;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}): void {
  if (!canTrack()) return;
  window.gtag?.('event', eventName, params);
}
